// src/redux/slices/subscriptionSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../api/authApi';

// ==================== ASYNC THUNKS ====================

// ✅ POST - Create Monthly Subscription - WITH meal_type and bookings
export const createSubscriptionAsync = createAsyncThunk(
  'subscription/create',
  async (subscriptionData, { rejectWithValue }) => {
    try {
      console.log('📤 MONTHLY SUBSCRIPTION PAYLOAD:', JSON.stringify(subscriptionData, null, 2));
      
      // ✅ Format items with meal_type
      const formattedData = {
        start_date: subscriptionData.start_date,
        delivery_address: subscriptionData.delivery_address || 'Address not provided',
        items: subscriptionData.items.map(item => ({
          meal_time: item.meal_time,
          meal_type: item.meal_type || 'Veg',
          quantity: item.quantity || 1
        })),
        bookings: (subscriptionData.bookings || []).map(booking => ({
          date: booking.date,
          items: (booking.items || []).map(item => ({
            meal_time: item.meal_time,
            meal_type: item.meal_type || 'Veg',
            quantity: item.quantity || 1
          }))
        }))
      };
      
      const response = await apiService.createSubscription(formattedData);
      console.log('✅ Subscription created:', response);
      return response;
    } catch (error) {
      console.error('❌ Create subscription error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to create subscription');
    }
  }
);

// ✅ GET - Get All Subscriptions
export const getSubscriptionListAsync = createAsyncThunk(
  'subscription/getList',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching subscription list...');
      const response = await apiService.getSubscriptions();
      console.log('✅ Subscription list:', response);
      return response;
    } catch (error) {
      console.error('❌ Get subscription list error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to fetch subscriptions');
    }
  }
);

// ✅ GET - Get Subscription Details
export const getSubscriptionDetailsAsync = createAsyncThunk(
  'subscription/getDetails',
  async (subscriptionId, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching subscription details:', subscriptionId);
      const response = await apiService.getSubscriptionDetails(subscriptionId);
      console.log('✅ Subscription details:', response);
      return response;
    } catch (error) {
      console.error('❌ Get subscription details error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to fetch subscription details');
    }
  }
);

// ✅ POST - Cancel Subscription
export const cancelSubscriptionAsync = createAsyncThunk(
  'subscription/cancel',
  async (subscriptionId, { rejectWithValue, dispatch }) => {
    try {
      console.log('🔄 Cancelling subscription:', subscriptionId);
      const response = await apiService.cancelSubscription(subscriptionId);
      console.log('✅ Subscription cancelled:', response);
      
      await dispatch(getSubscriptionListAsync());
      
      return response;
    } catch (error) {
      console.error('❌ Cancel subscription error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to cancel subscription');
    }
  }
);

// ✅ POST - Mark Meal Off
export const markMealOffAsync = createAsyncThunk(
  'subscription/markMealOff',
  async ({ subscriptionId, date, mealTime, mealType }, { rejectWithValue, dispatch }) => {
    try {
      console.log('🔄 Marking meal off:', { subscriptionId, date, mealTime, mealType });
      const response = await apiService.markMealOff(subscriptionId, date, mealTime, mealType);
      console.log('✅ Meal marked off:', response);
      
      await dispatch(getSubscriptionDetailsAsync(subscriptionId));
      
      return response;
    } catch (error) {
      console.error('❌ Mark meal off error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to mark meal off');
    }
  }
);

// ✅ DELETE - Remove Meal Off
export const removeMealOffAsync = createAsyncThunk(
  'subscription/removeMealOff',
  async ({ subscriptionId, date, mealTime, mealType }, { rejectWithValue, dispatch }) => {
    try {
      console.log('🔄 Removing meal off:', { subscriptionId, date, mealTime, mealType });
      const response = await apiService.removeMealOff(subscriptionId, date, mealTime, mealType);
      console.log('✅ Meal off removed:', response);
      
      await dispatch(getSubscriptionDetailsAsync(subscriptionId));
      
      return response;
    } catch (error) {
      console.error('❌ Remove meal off error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to remove meal off');
    }
  }
);

// ✅ GET - Weekly Menu
export const getWeeklyMenuAsync = createAsyncThunk(
  'subscription/getWeeklyMenu',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching weekly menu...');
      const response = await apiService.getWeeklyMenu();
      console.log('✅ Weekly menu:', response);
      return response;
    } catch (error) {
      console.error('❌ Get weekly menu error:', error);
      return rejectWithValue(typeof error === 'string' ? error : 'Failed to fetch weekly menu');
    }
  }
);

// ==================== INITIAL STATE ====================

const initialState = {
  subscriptions: [],
  currentSubscription: null,
  weeklyMenu: [],
  isLoading: false,
  isSubscribing: false,
  error: null,
  subscriptionStatus: null,
  mealOffs: [],
};

// ==================== SLICE ====================

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSubscriptionStatus: (state) => {
      state.subscriptionStatus = null;
    },
    resetSubscription: (state) => {
      state.subscriptions = [];
      state.currentSubscription = null;
      state.weeklyMenu = [];
      state.error = null;
      state.subscriptionStatus = null;
      state.mealOffs = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Subscription
      .addCase(createSubscriptionAsync.pending, (state) => {
        state.isSubscribing = true;
        state.error = null;
        state.subscriptionStatus = null;
      })
      .addCase(createSubscriptionAsync.fulfilled, (state, action) => {
        state.isSubscribing = false;
        state.subscriptionStatus = 'success';
        state.error = null;
        console.log('✅ Subscription created:', action.payload);
      })
      .addCase(createSubscriptionAsync.rejected, (state, action) => {
        state.isSubscribing = false;
        state.subscriptionStatus = 'failed';
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to create subscription';
      })
      
      // Get All Subscriptions
      .addCase(getSubscriptionListAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSubscriptionListAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscriptions = action.payload?.data || [];
        state.error = null;
        console.log('✅ Subscriptions loaded:', state.subscriptions.length);
      })
      .addCase(getSubscriptionListAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to fetch subscriptions';
      })
      
      // Get Subscription Details
      .addCase(getSubscriptionDetailsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSubscriptionDetailsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSubscription = action.payload?.data || null;
        state.mealOffs = action.payload?.data?.meal_offs || [];
        state.error = null;
        console.log('✅ Subscription details loaded');
      })
      .addCase(getSubscriptionDetailsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to fetch subscription details';
      })
      
      // Cancel Subscription
      .addCase(cancelSubscriptionAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelSubscriptionAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        console.log('✅ Subscription cancelled:', action.payload);
      })
      .addCase(cancelSubscriptionAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to cancel subscription';
      })
      
      // Mark Meal Off
      .addCase(markMealOffAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(markMealOffAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.mealOffs = action.payload?.data?.meal_offs || state.mealOffs;
        state.error = null;
        console.log('✅ Meal marked off:', action.payload);
      })
      .addCase(markMealOffAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to mark meal off';
      })
      
      // Remove Meal Off
      .addCase(removeMealOffAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeMealOffAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        console.log('✅ Meal off removed:', action.payload);
      })
      .addCase(removeMealOffAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to remove meal off';
      })
      
      // Weekly Menu
      .addCase(getWeeklyMenuAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getWeeklyMenuAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.weeklyMenu = action.payload?.data || [];
        state.error = null;
        console.log('✅ Weekly menu loaded:', state.weeklyMenu.length);
      })
      .addCase(getWeeklyMenuAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to fetch weekly menu';
      });
  },
});

// ==================== EXPORT ACTIONS & REDUCER ====================

export const {
  clearError,
  clearSubscriptionStatus,
  resetSubscription,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;